import { QueryClient } from '@tanstack/react-query';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import {
	MarginInfoByUserKey,
	PositionsByUserKey,
	OrdersByUserKey,
	LeftoverIsolatedByUserKey,
} from 'src/hooks/query/accountTradingData/utils/types';

interface AccountFreshnessEntry {
	/** Wall clock at the last push whose account slot had advanced. */
	lastFreshMs: number;
	/** Account slot recorded at that push. */
	lastSlot: number;
}

/**
 * When each userKey last received NEW chain data.
 *
 * The trading data caches are push-only (`enabled: false`,
 * `staleTime: Infinity`), so React Query itself has no notion of how old a row
 * is. This registry is that missing signal: it is the wall-clock floor the
 * freshness watchdog applies on top of its slot-lag comparison.
 *
 * The timestamp deliberately tracks the account slot rather than the push
 * itself. Most pushes are 1s polls recomputing from the SDK's local cache; if
 * they counted as freshness, a `User` whose socket died silently would look
 * fresh forever and the watchdog could never fire.
 */
const freshnessByUserKey = new Map<string, AccountFreshnessEntry>();

export function markAccountTradingDataPushed(
	userKey: string,
	accountSlot: number | undefined,
	nowMs: number = Date.now()
): void {
	// A failed/absent slot read is not evidence of new chain data, so it must not
	// seed the watchdog's grace period either.
	if (accountSlot === undefined) return;

	const prev = freshnessByUserKey.get(userKey);

	if (prev && accountSlot <= prev.lastSlot) return;

	freshnessByUserKey.set(userKey, {
		lastFreshMs: nowMs,
		lastSlot: accountSlot,
	});
}

export function getAccountTradingDataLastFreshMs(
	userKey: string
): number | undefined {
	return freshnessByUserKey.get(userKey)?.lastFreshMs;
}

/**
 * How long a subscription must stay down before its cached rows are dropped.
 *
 * Eviction blanks the positions table, so a momentary `isSubscribed === false`
 * must not trigger it. The window spans more than one orchestrator health tick,
 * giving a flapping subscription time to come back and cancel the eviction.
 */
export const ACCOUNT_TRADING_DATA_EVICTION_GRACE_MS = 6_000;

const pendingEvictionTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Evict unless the account's sync comes back within the grace window. */
export function scheduleAccountTradingDataEviction(
	queryClient: QueryClient,
	userKey: string,
	delayMs: number = ACCOUNT_TRADING_DATA_EVICTION_GRACE_MS
): void {
	if (pendingEvictionTimers.has(userKey)) return;

	pendingEvictionTimers.set(
		userKey,
		setTimeout(() => {
			pendingEvictionTimers.delete(userKey);
			evictAccountTradingData(queryClient, userKey);
		}, delayMs)
	);
}

export function cancelAccountTradingDataEviction(userKey: string): void {
	const timer = pendingEvictionTimers.get(userKey);
	if (timer === undefined) return;
	clearTimeout(timer);
	pendingEvictionTimers.delete(userKey);
}

/**
 * Drop a userKey's rows from all four trading data caches.
 *
 * Called when that account's sync has been stopped for the grace window
 * (unmount, or the `User` losing its subscription). Without it the last pushed
 * values sit in the cache forever with nothing to evict or invalidate them, and
 * the UI keeps rendering pre-liquidation sizes as fact. Empty/loading is the
 * honest state.
 */
export function evictAccountTradingData(
	queryClient: QueryClient,
	userKey: string
): void {
	cancelAccountTradingDataEviction(userKey);
	freshnessByUserKey.delete(userKey);

	const removeUserKey = <T>(prev: Record<string, T> | undefined) => {
		if (!prev || !(userKey in prev)) return prev;
		const next = { ...prev };
		delete next[userKey];
		return next;
	};

	queryClient.setQueryData<MarginInfoByUserKey>(
		accountTradingDataQueryKeys.marginInfo(),
		removeUserKey
	);
	queryClient.setQueryData<PositionsByUserKey>(
		accountTradingDataQueryKeys.positions(),
		removeUserKey
	);
	queryClient.setQueryData<OrdersByUserKey>(
		accountTradingDataQueryKeys.orders(),
		removeUserKey
	);
	queryClient.setQueryData<LeftoverIsolatedByUserKey>(
		accountTradingDataQueryKeys.leftoverIsolatedPositions(),
		removeUserKey
	);
}
